import { X } from "lucide-react";
import type { ReactNode } from "react";

import { IconButton } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import type { HelpScreen } from "../help/topics";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";

/** Anatomie commune des fiches d'objet de la colonne droite (unité, ligne, graphique, objet de la carte) : en-tête
 *  à icône (`FloatingPanel icon`) avec titre et sous-titre, « ? » puis la croix, propriétés en `PropRow`,
 *  sections en `CardSection`, actions dans le pied (`footer`, `Button secondary sm` avec icône). */

/** Titre d'une fiche : nom quasi noir et, dessous, sous-titre gris (type d'objet, rattachement). Avec un
 *  sous-titre, la fiche passe `tallHeader`. */
export function CardTitle({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="truncate">{title}</span>
      {subtitle ? <span className="truncate text-meta font-normal text-ink-muted">{subtitle}</span> : null}
    </span>
  );
}

/** Actions d'en-tête d'une fiche : aide (« ? », fiche d'aide `topic` de l'écran `screen`) puis fermeture, en
 *  28 px. */
export function CardActions({
  topic = "carte.fiche",
  screen = "carte",
  children,
}: {
  topic?: string;
  screen?: HelpScreen;
  children?: ReactNode;
}) {
  const select = useUi((s) => s.select);
  return (
    <>
      {children}
      <HelpButton screen={screen} topic={topic} buttonSize="sm" size={16} />
      <IconButton size="sm" onClick={() => select(null)} title={t("Fermer")} aria-label={t("Fermer")}>
        <X size={16} />
      </IconButton>
    </>
  );
}

/** Pied d'actions d'une fiche : boutons à la ligne au besoin (libellés longs en anglais). */
export function CardFooter({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>;
}
